import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, ExternalLink, Send, Edit3, Trash2 } from "lucide-react";
import { API, usePublicConfig } from "../App";
import { Button } from "./ui/button";

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`p-0.5 transition-transform hover:scale-110 ${n <= value ? "text-[#C5A059]" : "text-white/20"}`}
          data-testid={`star-${n}`}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star className={`w-6 h-6 ${n <= value ? "fill-[#C5A059]" : ""}`} />
        </button>
      ))}
    </div>
  );
}

/**
 * Eligible: confirmed/completed/proposal_accepted bookings whose pickup was 24h-7d ago.
 * Renders nothing if outside the window.
 */
function isReviewEligible(quote) {
  if (!["confirmed", "completed", "proposal_accepted"].includes(quote.status)) return false;
  if (!quote.pickup_date || !quote.pickup_time) return false;
  const pickup = new Date(`${quote.pickup_date}T${quote.pickup_time}:00`);
  if (Number.isNaN(pickup.getTime())) return false;
  const ageMs = Date.now() - pickup.getTime();
  return ageMs >= 24 * 60 * 60 * 1000 && ageMs <= 7 * 24 * 60 * 60 * 1000;
}

export default function ReviewPanel({ quote }) {
  const { google_business_url } = usePublicConfig();
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!isReviewEligible(quote)) { setLoading(false); return; }
    let alive = true;
    axios.get(`${API}/quotes/${quote.quote_id}/review`)
      .then((res) => {
        if (!alive) return;
        setExisting(res.data || null);
        if (res.data) {
          setRating(res.data.rating);
          setComment(res.data.comment || "");
        }
      })
      .catch((err) => { console.warn("Failed to load review", err?.response?.status); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [quote]);

  const remove = async () => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await axios.delete(`${API}/quotes/${quote.quote_id}/review`);
      setExisting(null); setRating(0); setComment(""); setEditing(false);
      toast.success("Review deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete review");
    }
  };

  if (loading) return null;
  if (!isReviewEligible(quote)) return null;

  // Already reviewed → show summary + edit/delete actions (or the edit form if user clicked Edit)
  if (existing && !editing) {
    return (
      <div className="mt-4 border-t border-white/5 pt-4 flex items-center justify-between gap-3" data-testid={`review-summary-${quote.quote_id}`}>
        <div>
          <div className="flex items-center gap-1 mb-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-4 h-4 ${n <= existing.rating ? "fill-[#C5A059] text-[#C5A059]" : "text-white/20"}`} />
            ))}
          </div>
          {existing.comment && <p className="text-white/60 text-xs italic">"{existing.comment}"</p>}
          {existing.replied_at && (
            <p className="text-emerald-400 text-[10px] mt-1">\u00b7 The TaxiNearMe team replied to your review</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditing(true)}
            className="text-white/40 hover:text-[#C5A059] text-[11px] uppercase tracking-wide flex items-center gap-1"
            data-testid={`edit-review-${quote.quote_id}`}
          >
            <Edit3 className="w-3 h-3" /> Edit
          </button>
          <button
            onClick={remove}
            className="text-white/40 hover:text-red-400 text-[11px] uppercase tracking-wide flex items-center gap-1"
            data-testid={`delete-review-${quote.quote_id}`}
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { toast.error("Please pick a rating between 1 and 5"); return; }
    setSubmitting(true);
    try {
      const isUpdate = !!existing;
      const url = `${API}/quotes/${quote.quote_id}/review`;
      const body = { rating, comment: comment.trim() || null };
      const res = isUpdate
        ? await axios.put(url, body)
        : await axios.post(url, body);
      setExisting(res.data);
      setEditing(false);
      toast.success(isUpdate ? "Review updated" : "Thanks for your review!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="mt-4 border-t border-white/5 pt-4 space-y-3" data-testid={`review-form-${quote.quote_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[#C5A059] text-xs uppercase tracking-wide font-semibold mb-1">How was your transfer?</p>
          <StarPicker value={rating} onChange={setRating} />
        </div>
        {google_business_url && (
          <a
            href={google_business_url}
            target="_blank"
            rel="noreferrer"
            className="text-white/60 hover:text-[#C5A059] text-xs flex items-center gap-1"
            data-testid={`google-review-link-${quote.quote_id}`}
          >
            <ExternalLink className="w-3 h-3" /> Or leave a Google review
          </a>
        )}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Optional comment (max 2000 chars)"
        maxLength={2000}
        className="w-full bg-[#1A1A1A] border border-[#333] rounded-sm p-2 text-white/80 text-xs resize-none h-16 focus:border-[#C5A059]/50 focus:outline-none"
        data-testid={`review-comment-${quote.quote_id}`}
      />
      <Button type="submit" size="sm" disabled={submitting || !rating} className="btn-gold" data-testid={`submit-review-${quote.quote_id}`}>
        <Send className="w-4 h-4 mr-1" /> {submitting ? "Saving..." : (existing ? "Update review" : "Submit review")}
      </Button>
      {editing && (
        <Button type="button" size="sm" variant="ghost" onClick={() => { setEditing(false); setRating(existing.rating); setComment(existing.comment || ""); }} className="ml-2 text-white/60">
          Cancel
        </Button>
      )}
    </form>
  );
}
